import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Col, Input, Popconfirm, Row } from 'antd';
import { CheckOutlined, CloseOutlined, PlayCircleOutlined, SendOutlined, StopOutlined } from '@ant-design/icons';
import { toast } from "react-toastify";
import { useSelector } from 'react-redux';
import { ContentHeader } from "../../components";
import ModalTextForm from "../../components/modal/ModalTextForm";
import { Links } from "../../helpers/Links";
import { maChucVu, responseStatus } from "../../constants";
import { DotKiemKeTaiSan, TrangThaiDotKiemKe } from "../../models/taisan/DotKiemKeTaiSan";
import LoadingDetailPage from "../../modules/loading/LoadingDetailPage";
import { layChiTietDotKiemKe, trinhDuyetDotKiemKe, thuHoiDotKiemKe, pheDuyetDotKiemKe, capNhatTrangThaiDotKiemKe } from "../../services/dotKiemKeTaiSan";
import LuuDotKiemKeBox from "./LuuDotKiemKeBox";

const ChiTietDotKiemKe = () => {
    const navigate = useNavigate();
    const user = useSelector((state: any) => state.auth.currentUser);
    const [dotKiemKe, setDotKiemKe] = useState<DotKiemKeTaiSan | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [confirmModal, setConfirmModal] = useState<any>({});
    const [lyDo, setLyDo] = useState<string>("");

    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');

    const isThemMoi = !id;

    const loadChiTiet = async () => {
        if (!id) return;

        try {
            setIsLoading(true);
            const result = await layChiTietDotKiemKe(parseInt(id));
            setDotKiemKe(result);
        } catch (error: any) {
            toast.error('Lỗi khi tải chi tiết đợt kiểm kê: ' + error.message);
            navigate(Links.TaiSan.DotKiemKe);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmitSuccess = () => {
        navigate(Links.TaiSan.DotKiemKe);
    };

    useEffect(() => {
        if (id) {
            loadChiTiet();
        }
    }, [id]);

    const handleTrinhDuyet = async () => {
        try {
            setIsLoading(true);
            const response = await trinhDuyetDotKiemKe(parseInt(id!));
            if (response.trangThai === responseStatus.thanhCong) {
                toast.success('Trình duyệt thành công');
                loadChiTiet();
            } else {
                toast.error(response.thongBao);
            }
        } catch (error: any) {
            toast.error(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleThuHoi = async (currentLyDo?: string) => {
        try {
            setIsLoading(true);
            const response = await thuHoiDotKiemKe(parseInt(id!), currentLyDo || "Thu hồi đợt kiểm kê");
            if (response.trangThai === responseStatus.thanhCong) {
                toast.success('Thu hồi thành công');
                loadChiTiet();
            } else {
                toast.error(response.thongBao);
            }
        } catch (error: any) {
            toast.error(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePheDuyet = async (isDuyet: boolean, currentLyDo?: string) => {
        try {
            setIsLoading(true);
            const response = await pheDuyetDotKiemKe(parseInt(id!), isDuyet, currentLyDo || (isDuyet ? "Đồng ý phê duyệt" : "Từ chối phê duyệt"));
            if (response.trangThai === responseStatus.thanhCong) {
                toast.success(isDuyet ? 'Phê duyệt thành công' : 'Từ chối thành công');
                loadChiTiet();
            } else {
                toast.error(response.thongBao);
            }
        } catch (error: any) {
            toast.error(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBatDauKiemKe = async () => {
        try {
            setIsLoading(true);
            await capNhatTrangThaiDotKiemKe(parseInt(id!), TrangThaiDotKiemKe.DangKiemKe);
            toast.success('Bắt đầu kiểm kê thành công');
            loadChiTiet();
        } catch (error: any) {
            toast.error(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleHoanThanhKiemKe = async () => {
        try {
            setIsLoading(true);
            await capNhatTrangThaiDotKiemKe(parseInt(id!), TrangThaiDotKiemKe.HoanThanh);
            toast.success('Hoàn thành kiểm kê thành công');
            loadChiTiet();
        } catch (error: any) {
            toast.error(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const isCreator = dotKiemKe && dotKiemKe.idNVKhoiTao === user?.id;
    const isNguoiPhuTrach = dotKiemKe && (dotKiemKe.idsDSNguoiPhuTrach?.length === 0 || !dotKiemKe.idsDSNguoiPhuTrach || dotKiemKe.idsDSNguoiPhuTrach.includes(user?.idNhanVien) || dotKiemKe.idsDSNguoiPhuTrach.includes(user?.id));
    const canManageKiemKe = !!(isCreator || isNguoiPhuTrach);
    const canTrinhDuyet = dotKiemKe && [TrangThaiDotKiemKe.KhoiTao, TrangThaiDotKiemKe.ThuHoi, TrangThaiDotKiemKe.PhoCVPTraLai, TrangThaiDotKiemKe.CVPTraLai, TrangThaiDotKiemKe.PhoGiamDocTraLai, TrangThaiDotKiemKe.GiamDocTraLai].includes(dotKiemKe.trangThai) && isCreator;
    const canThuHoi = dotKiemKe && [TrangThaiDotKiemKe.TrinhPhoCVP, TrangThaiDotKiemKe.TrinhCVP, TrangThaiDotKiemKe.TrinhPhoGiamDoc, TrangThaiDotKiemKe.TrinhGiamDoc].includes(dotKiemKe.trangThai) && isCreator;
    const canBatDauKiemKe = dotKiemKe && dotKiemKe.trangThai === TrangThaiDotKiemKe.PheDuyet && canManageKiemKe;
    const canHoanThanhKiemKe = dotKiemKe && dotKiemKe.trangThai === TrangThaiDotKiemKe.DangKiemKe && canManageKiemKe;

    const isPhoCVP = !!dotKiemKe && user?.maChucVu === maChucVu.phoCVP && user?.idNhanVien === dotKiemKe.idPhoCVP && [TrangThaiDotKiemKe.TrinhPhoCVP, TrangThaiDotKiemKe.CVPTraLai].includes(dotKiemKe.trangThai);
    const isChanhVP = !!dotKiemKe && user?.maChucVu === maChucVu.chanhVP && user?.idNhanVien === dotKiemKe.idChanhVP && [TrangThaiDotKiemKe.TrinhCVP, TrangThaiDotKiemKe.PhoGiamDocTraLai, TrangThaiDotKiemKe.GiamDocTraLai].includes(dotKiemKe.trangThai);
    const isPhoGiamDoc = !!dotKiemKe && user?.maChucVu === maChucVu.phoGiamDoc && user?.idNhanVien === dotKiemKe.idPhoGiamDoc && [TrangThaiDotKiemKe.TrinhPhoGiamDoc, TrangThaiDotKiemKe.GiamDocTraLai].includes(dotKiemKe.trangThai);
    const isGiamDoc = !!dotKiemKe && user?.maChucVu === maChucVu.giamDoc && user?.idNhanVien === dotKiemKe.idGiamDoc && [TrangThaiDotKiemKe.TrinhGiamDoc].includes(dotKiemKe.trangThai);

    const openModal = (title: string, content: React.ReactNode, onSubmit: (currentLyDo: string) => void, showLyDo = false, batBuocLyDo = false) => {
        setLyDo("");
        setConfirmModal({
            isShowModal: true,
            title,
            content,
            showLyDo,
            batBuocLyDo,
            onSubmit,
        });
    };

    const renderActionButtons = () => {
        if (!dotKiemKe) {
            return null;
        }

        return (
            <Row style={{ marginBottom: 16 }}>
                <Col span={24} style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    {canTrinhDuyet && (
                        <Button
                            type="primary"
                            icon={<SendOutlined rev="" />}
                            onClick={() => openModal(
                                "Trình duyệt đợt kiểm kê",
                                <>Bạn có muốn <strong>trình duyệt</strong> đợt kiểm kê này không?</>,
                                () => handleTrinhDuyet()
                            )}
                        >
                            Trình duyệt
                        </Button>
                    )}
                    {canThuHoi && (
                        <Button
                            danger
                            onClick={() => openModal(
                                "Thu hồi đợt kiểm kê",
                                <>Bạn có muốn <strong>thu hồi</strong> đợt kiểm kê này không?</>,
                                (currentLyDo) => handleThuHoi(currentLyDo),
                                true,
                                false
                            )}
                        >
                            Thu hồi
                        </Button>
                    )}
                    {(isPhoCVP || isChanhVP || isPhoGiamDoc || isGiamDoc) && (
                        <>
                            <Button
                                type="primary"
                                icon={<CheckOutlined rev="" />}
                                onClick={() => openModal(
                                    "Phê duyệt đợt kiểm kê",
                                    <>Bạn có muốn <strong>phê duyệt</strong> đợt kiểm kê này không?</>,
                                    (currentLyDo) => handlePheDuyet(true, currentLyDo),
                                    true,
                                    false
                                )}
                            >
                                Phê duyệt
                            </Button>
                            <Button
                                danger
                                icon={<CloseOutlined rev="" />}
                                onClick={() => openModal(
                                    "Từ chối đợt kiểm kê",
                                    <>Bạn có muốn <strong>từ chối</strong> đợt kiểm kê này không?</>,
                                    (currentLyDo) => handlePheDuyet(false, currentLyDo),
                                    true,
                                    true
                                )}
                            >
                                Từ chối
                            </Button>
                        </>
                    )}
                    {canBatDauKiemKe && (
                        <Popconfirm
                            title="Bắt đầu kiểm kê sẽ chuyển trạng thái đợt sang Đang kiểm kê. Xác nhận?"
                            onConfirm={handleBatDauKiemKe}
                            okText="Xác nhận"
                            cancelText="Hủy"
                        >
                            <Button type="primary" icon={<PlayCircleOutlined />}>
                                Bắt đầu kiểm kê
                            </Button>
                        </Popconfirm>
                    )}
                    {canHoanThanhKiemKe && (
                        <Popconfirm
                            title="Xác nhận hoàn thành kiểm kê? Sau khi hoàn thành sẽ không thể cập nhật kết quả nữa."
                            onConfirm={handleHoanThanhKiemKe}
                            okText="Hoàn thành"
                            cancelText="Hủy"
                        >
                            <Button icon={<StopOutlined />} style={{ background: '#52c41a', borderColor: '#52c41a', color: '#fff' }}>
                                Hoàn thành kiểm kê
                            </Button>
                        </Popconfirm>
                    )}
                </Col>
            </Row>
        );
    };

    if (isLoading && id) {
        return <LoadingDetailPage title={isThemMoi ? "Thêm mới đợt kiểm kê" : "Chỉnh sửa đợt kiểm kê"} />;
    }

    return (
        <section>
            <ContentHeader title={isThemMoi ? "Thêm mới đợt kiểm kê" : "Chỉnh sửa đợt kiểm kê"} />
            <ModalTextForm
                show={!!confirmModal.isShowModal}
                title={confirmModal.title}
                handleClose={() => setConfirmModal({ isShowModal: false })}
                handleSave={() => {
                    if (confirmModal.batBuocLyDo && !lyDo.trim()) {
                        toast.warning("Vui lòng nhập lý do từ chối");
                        return;
                    }
                    confirmModal.onSubmit(lyDo);
                    setConfirmModal({ isShowModal: false });
                }}
            >
                <Row>
                    <Col span={24}>
                        {confirmModal.content}
                    </Col>
                </Row>
                {confirmModal.showLyDo && (
                    <Row>
                        <Col span={24} className="form-group">
                            <label>Lý do {confirmModal.batBuocLyDo && <span className="text-red">*</span>}</label>
                            <Input.TextArea
                                rows={2}
                                value={lyDo}
                                onChange={(e) => setLyDo(e.target.value)}
                            />
                        </Col>
                    </Row>
                )}
            </ModalTextForm>
            <Row>
                <Col span={24}>
                    <div className="card">
                        <div className="card-body">
                            {renderActionButtons()}
                            <LuuDotKiemKeBox
                                modelProps={dotKiemKe}
                                onSubmit={handleSubmitSuccess}
                            />
                        </div>
                    </div>
                </Col>
            </Row>
        </section>
    );
};

export default ChiTietDotKiemKe;
